
    import __i18nConfig from '@next-translate-root/i18n.js'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPaths, GetStaticPropsContext, GetStaticPropsResult } from "next";
import { getPageData, PageProps } from "@lib/frontend/pages/webPage";
import { StrapiPages } from "@lib/strapi/pages";
import { removeEmptyAttributes } from "@lib/strapi/content";
import { compileEntitySEOInformation } from "@lib/utils/seo";
import ModularPage from "@components/layout/pages/ModularPage";
import { TreatmentService } from "@lib/strapi/services/content-types/treatment.service";
import { ConditionService } from "@lib/strapi/services/content-types/condition.service";
import { TeamMemberService } from "@lib/strapi/services/content-types/teamMember.service";
/**
 * The root-level catch-all for treatments, conditions AND team members
 * (D18 / D35).
 *
 * Item 9 removed the category segment, so `/tecar-therapy`, `/osfyalgia` and
 * `/marina-papakonstantopoulou` all resolve here. Next allows exactly one
 * `pages/[slug].tsx` and the engine's `staticPaths` takes a single source
 * content type, so this route is hand-written rather than generated — the
 * web-page instances behind it (`treatment-detail`, `condition-detail`,
 * `team-member-detail`) supply the page shell, and this decides which one a
 * given slug gets.
 *
 * TWO THINGS THAT MUST HOLD, or a page silently disappears:
 *
 *   1. Slugs are unique ACROSS all three collections. A slug present in two
 *      would resolve to whichever is checked first and shadow the other.
 *      Guarded at build time below — a collision fails the build rather than
 *      shipping a missing page.
 *   2. Fixed routes must never be swallowed. Next resolves a concrete file
 *      (`/epikoinonia.tsx`) before a dynamic one, so the framework protects us
 *      — but only for routes that exist as files. RESERVED lists them so a
 *      treatment slugged "blog" is caught here rather than quietly winning or
 *      losing a race with a page someone adds later.
 */
const RESERVED = new Set([
    "index", "to-fysikotherapeftirio", "therapeies", "pathiseis",
    "plano-therapeias", "apantiseis-se-syxna-erotimata", "epikoinonia",
    "efkairies-ergasias", "blog", "politiki-aporritou", "politiki-cookies",
    "efcharistoume", "kleiste-rantevou", "search", "oroi-xrisis", "api", "404", "500",
]);
export default function EntityPage(props: PageProps) {
    return <ModularPage {...props}/>;
}
export const getStaticPaths: GetStaticPaths = async ({ locales = ["el"] }) => {
    const paths: {
        params: {
            slug: string;
        };
        locale: string;
    }[] = [];
    for (const locale of locales) {
        const [treatments, conditions, members] = await Promise.all([
            TreatmentService.fetchAll(null, locale),
            ConditionService.fetchAll(null, locale),
            TeamMemberService.fetchAll(null, locale),
        ]);
        const bySource: [
            string,
            string[]
        ][] = [
            ["treatments", (treatments ?? []).map((t: any) => t?.slug).filter(Boolean)],
            ["conditions", (conditions ?? []).map((c: any) => c?.slug).filter(Boolean)],
            ["team members", (members ?? []).map((m: any) => m?.slug).filter(Boolean)],
        ];
        const owner = new Map<string, string>();
        for (const [source, slugs] of bySource) {
            for (const slug of slugs) {
                const other = owner.get(slug);
                if (other) {
                    throw new Error(`[${locale}] slug collision between ${other} and ${source}: "${slug}". ` +
                        `All resolve at /[slug], so one would shadow the other — rename one before publishing.`);
                }
                owner.set(slug, source);
            }
        }
        const reserved = [...owner.keys()].filter((s) => RESERVED.has(s));
        if (reserved.length) {
            throw new Error(`[${locale}] slug(s) collide with a fixed route: ${reserved.join(", ")}. ` +
                `Rename them — a fixed page wins and the entry becomes unreachable.`);
        }
        for (const slug of owner.keys()) {
            paths.push({ params: { slug }, locale });
        }
    }
    // 'blocking' rather than false: an entry published after the last build
    // renders on first request instead of 404ing until the next deploy.
    return { paths, fallback: "blocking" };
};
const getStaticProps = async (context: GetStaticPropsContext): Promise<GetStaticPropsResult<PageProps>> => {
    const slug = String((context.params as {
        slug?: string;
    })?.slug ?? "");
    const locale = context.locale ?? "el";
    const treatment = await TreatmentService.fetchBySlug(null, locale, slug);
    if (treatment) {
        const page = await getPageData(context, StrapiPages.treatmentDetailTreatment);
        if ("props" in page) {
            // getStaticProps must return JSON-serializable props; the service DTOs
            // carry `undefined` for unset fields, which Next refuses to serialize.
            // The page props already go through this — the entity needs it too.
            return {
                props: removeEmptyAttributes({
                    ...page.props,
                    // the shell's seo is generic per type — the entity's own wins
                    seo: await compileEntitySEOInformation(context, treatment as never),
                    entity: treatment,
                    entityKind: "treatment",
                }) as PageProps,
                // sidebar reviews strip refreshes with the same ~6h ISR as the shells
                revalidate: 21600,
            };
        }
        return page;
    }
    const condition = await ConditionService.fetchBySlug(null, locale, slug);
    if (condition) {
        const page = await getPageData(context, StrapiPages.conditionDetailCondition);
        if ("props" in page) {
            return {
                props: removeEmptyAttributes({
                    ...page.props,
                    // the shell's seo is generic per type — the entity's own wins
                    seo: await compileEntitySEOInformation(context, condition as never),
                    entity: condition,
                    entityKind: "condition",
                }) as PageProps,
                // sidebar reviews strip refreshes with the same ~6h ISR as the shells
                revalidate: 21600,
            };
        }
        return page;
    }
    const member = await TeamMemberService.fetchBySlug(null, locale, slug);
    if (member) {
        const page = await getPageData(context, StrapiPages.teamMemberDetail);
        if ("props" in page) {
            return {
                props: removeEmptyAttributes({
                    ...page.props,
                    // the shell's seo is generic per type — the entity's own wins
                    seo: await compileEntitySEOInformation(context, member as never),
                    entity: member,
                    entityKind: "team",
                }) as PageProps,
                // sidebar reviews strip refreshes with the same ~6h ISR as the shells
                revalidate: 21600,
            };
        }
        return page;
    }
    return { notFound: true };
};

    async function __Next_Translate__getStaticProps__1a09d3969c8__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a09d3969c8__ as getStaticProps }
  